import type { Analytics } from "firebase/analytics";
import { FirebaseAnalytics } from "@shared/src/utils/analytics/FirebaseAnalytics";
import { ClarityAnalytics } from "@shared/src/utils/analytics/ClarityAnalytics";

interface IxAnalyticsInitOpts {
  firebase?: Analytics | null;
  clarityProjectId?: string;
}

export class IxAnalytics {
  private static _instance: IxAnalytics | null = null;

  firebase?: FirebaseAnalytics;
  clarity?: ClarityAnalytics;

  private constructor(opts: IxAnalyticsInitOpts) {
    if (opts.firebase) this.firebase = new FirebaseAnalytics(opts.firebase);
    if (opts.clarityProjectId) this.clarity = new ClarityAnalytics(opts.clarityProjectId);
  }

  static init(opts: IxAnalyticsInitOpts): IxAnalytics {
    if (!opts.firebase && !opts.clarityProjectId) {
      throw new Error("IxAnalytics.init: provide at least one analytics provider");
    }
    if (!IxAnalytics._instance) {
      IxAnalytics._instance = new IxAnalytics(opts);
    }
    return IxAnalytics._instance;
  }

  static get instance(): IxAnalytics {
    if (!IxAnalytics._instance) throw new Error("IxAnalytics not initialised");
    return IxAnalytics._instance;
  }

  /* ------------------------------------------------------------ */
  /* Generic helpers                                              */
  /* ------------------------------------------------------------ */

  private ga = () => this.firebase; // shorthand
  private cl = () => this.clarity;

  /* ------------------------------------------------------------ */
  /* Business-level tracking API                                  */
  /* ------------------------------------------------------------ */

  trackAdCreation(_data: any, userId: string, username: string) {
    this.cl()?.identifyUser(userId, undefined, "create_ad", username);
    this.ga()?.setUserId(userId);
    this.cl()?.trackEvent("ad_created");
    this.ga()?.logEvent("ad_created");
  }

  trackAdEdit(_data: any, adId: string, userId: string, username: string) {
    this.cl()?.identifyUser(userId, undefined, "edit_ad", username);
    this.cl()?.trackEvent("ad_edited");
    this.ga()?.logEvent("ad_edited", { ad_id: adId });
  }

  trackOrderAction(action: string, orderData: any, adId: string, userId?: string) {
    if (!userId) return;
    this.cl()?.identifyUser(userId);
    this.cl()?.trackEvent(`order_${action}`);
    this.ga()?.logEvent(`order_${action}`, { order_id: orderData?.id, ad_id: adId });
  }

  trackPaymentConfirmation(orderId: string, userId: string) {
    this.cl()?.identifyUser(userId);
    this.cl()?.trackEvent("payment_confirmed");
    this.ga()?.logEvent("payment_confirmed", { order_id: orderId });
  }

  trackOrderCompletion(orderId: string, userId: string) {
    this.cl()?.identifyUser(userId);
    this.cl()?.trackEvent("order_completed");
    this.ga()?.logEvent("order_completed", { order_id: orderId });
  }

  trackOrderView(orderId: string, userId: string) {
    this.cl()?.identifyUser(userId);
    this.cl()?.trackEvent("order_viewed");
    this.ga()?.logEvent("order_viewed", { order_id: orderId });
  }

  trackSignupCompletion(userId: string, username: string, method: string) {
    this.cl()?.identifyUser(userId, undefined, "registration_complete", username);
    this.ga()?.setUserId(userId);
    this.ga()?.logEvent("signup_complete", { method });
  }

  trackTradeAttempt(details: any) {
    this.cl()?.trackEvent("trade_attempt");
    this.ga()?.logEvent("trade_attempt", details);
  }

  trackTradeButtonClick(details: any) {
    this.cl()?.trackEvent("trade_button_clicked");
    this.ga()?.logEvent("trade_button_clicked", details);
  }

  trackTradeAttemptFailed(details: any) {
    this.cl()?.trackEvent("trade_attempt_failed");
    this.ga()?.logEvent("trade_attempt_failed", details);
  }

  trackAutoAlchemySignout(userAddress: string, username: string) {
    this.cl()?.identifyUser(userAddress, undefined, "signout", username);
    this.cl()?.trackEvent("auto_alchemy_signout");
    this.ga()?.logEvent("auto_alchemy_signout");
  }

  trackAppRating(userId: string, rating: number) {
    this.cl()?.identifyUser(userId);
    this.ga()?.logEvent("app_rated", { rating });
  }
}
